import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useSelector from '@hooks/useSelector'

const watchEntityKeys = ['invoiceItems']

export type InvoiceItemsFilters = {
  invoice_id?: number,
}

interface UseInvoiceItemsOptions {
  entityKey?: string,
  filters?: InvoiceItemsFilters,
  performHttpRequests?: boolean,
}

function useInvoiceItems(options: UseInvoiceItemsOptions = {}) {
  const { filters = {} } = options
  const { invoice_id: filterInvoiceId } = filters

  const {
    updatedEntities: { invoiceItems: invoiceItemsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { invoiceItems, services } = useSelector(reduxState => reduxState.entities)

  const filteredInvoiceItems = useMemo(() => {
    const filtered = Object.values(invoiceItems).filter((invoiceItem) => {
      const { invoice_id } = invoiceItem

      const matchInvoiceId = matchFilterNumber(invoice_id, filterInvoiceId)

      return matchInvoiceId
    })

    return sortArrayBy(filtered, 'asc', invoiceItem => services[invoiceItem.service_id]?.title)
  }, [invoiceItemsUpdatedAt, JSON.stringify(filters)])

  const filteredInvoiceItemsCount = filteredInvoiceItems.length
  const hasFilteredInvoiceItems = !!filteredInvoiceItemsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredInvoiceItemsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
  } = loadMorePayload

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredInvoiceItemsCount,
    filteredInvoiceItems,
    hasFilteredInvoiceItems,
  }
}

export default useInvoiceItems
